// src/HeatmapOverlay.js
import React, { useMemo } from 'react';
import './HeatmapOverlay.css';

/**
 * HeatmapOverlay - Displays policy probability or saliency heatmaps over the game board
 *
 * @param {string} mode - Visualization mode: 'placement-policy', 'jump-policy', 'saliency-0' through 'saliency-5', or 'off'
 * @param {Object} nnData - Neural network data containing policyProbs, valueEstimate, saliencyMap
 * @param {number} boardRows - Number of rows on the board
 * @param {number} boardCols - Number of columns on the board
 * @param {number} cellSize - Size of each cell in pixels
 * @param {number} viewPerspective - Player perspective (1 or 2)
 */
const HeatmapOverlay = ({
    mode = 'off',
    nnData = null,
    boardRows = 15,
    boardCols = 21,
    cellSize = 40,
    viewPerspective = 1,
}) => {
    // Parse mode to extract visualization type and layer
    const { visualizationType, saliencyLayer } = useMemo(() => {
        if (mode.startsWith('saliency-')) {
            const layer = parseInt(mode.split('-')[1], 10);
            return { visualizationType: 'saliency', saliencyLayer: layer };
        } else if (mode === 'placement-policy') {
            return { visualizationType: 'placement', saliencyLayer: null };
        } else if (mode === 'jump-policy') {
            return { visualizationType: 'jump', saliencyLayer: null };
        }
        return { visualizationType: null, saliencyLayer: null };
    }, [mode]);

    // Process the data into a 2D heatmap array
    const heatmapData = useMemo(() => {
        if (!nnData || !visualizationType) return null;

        if (visualizationType === 'saliency') {
            // Extract saliency map for the specified layer
            const { saliencyMap } = nnData;
            if (!saliencyMap || saliencyLayer >= saliencyMap.length) return null;

            const layerData = saliencyMap[saliencyLayer];
            // layerData should be a 2D array [rows][cols]
            return layerData;
        } else if (visualizationType === 'placement') {
            // Extract placement policy from policyProbs
            const { policyProbs } = nnData;
            if (!policyProbs) return null;

            // policyProbs format: [0, rows*cols) = placement probs
            const placementProbs = policyProbs.slice(0, boardRows * boardCols);

            // Reshape into 2D array
            const heatmap = [];
            for (let r = 0; r < boardRows; r++) {
                const row = [];
                for (let c = 0; c < boardCols; c++) {
                    const index = r * boardCols + c;
                    row.push(placementProbs[index] || 0);
                }
                heatmap.push(row);
            }
            return heatmap;
        } else if (visualizationType === 'jump') {
            // Extract jump policy from policyProbs
            const { policyProbs } = nnData;
            if (!policyProbs) return null;

            // policyProbs format: [rows*cols, 2*rows*cols) = jump probs
            const jumpProbs = policyProbs.slice(boardRows * boardCols, 2 * boardRows * boardCols);

            // Reshape into 2D array
            const heatmap = [];
            for (let r = 0; r < boardRows; r++) {
                const row = [];
                for (let c = 0; c < boardCols; c++) {
                    const index = r * boardCols + c;
                    row.push(jumpProbs[index] || 0);
                }
                heatmap.push(row);
            }
            return heatmap;
        }

        return null;
    }, [nnData, visualizationType, saliencyLayer, boardRows, boardCols]);

    // Find min/max values for normalization
    const { minVal, maxVal } = useMemo(() => {
        if (!heatmapData) return { minVal: 0, maxVal: 1 };

        let min = Infinity;
        let max = -Infinity;

        for (let r = 0; r < heatmapData.length; r++) {
            for (let c = 0; c < heatmapData[r].length; c++) {
                const val = heatmapData[r][c];
                if (val < min) min = val;
                if (val > max) max = val;
            }
        }

        return { minVal: min, maxVal: max };
    }, [heatmapData]);

    // Get color for a value based on visualization type
    const getColor = (value, min, max) => {
        if (max === min) return 'rgba(0, 0, 0, 0)';

        // Normalize value to 0-1 range
        const normalized = (value - min) / (max - min);

        if (visualizationType === 'saliency') {
            // Blue to red gradient for saliency
            const r = Math.floor(normalized * 255);
            const b = Math.floor((1 - normalized) * 255);
            const opacity = 0.3 + normalized * 0.5; // 0.3 to 0.8
            return `rgba(${r}, 0, ${b}, ${opacity})`;
        } else if (visualizationType === 'placement') {
            // Cyan/blue gradient for placement policy
            const intensity = Math.floor(normalized * 255);
            const opacity = 0.2 + normalized * 0.6; // 0.2 to 0.8
            return `rgba(0, ${intensity}, 255, ${opacity})`;
        } else if (visualizationType === 'jump') {
            // Green gradient for jump policy
            const intensity = Math.floor(normalized * 255);
            const opacity = 0.2 + normalized * 0.6; // 0.2 to 0.8
            return `rgba(0, ${intensity}, 0, ${opacity})`;
        }

        return 'rgba(0, 0, 0, 0)';
    };

    if (mode === 'off' || !heatmapData) {
        return null;
    }

    const boardWidth = boardCols * cellSize;
    const boardHeight = boardRows * cellSize;

    return (
        <div className="heatmap-overlay" style={{ width: boardWidth, height: boardHeight }}>
            <svg width={boardWidth} height={boardHeight} className="heatmap-svg">
                {heatmapData.map((row, r) => (
                    row.map((value, c) => {
                        // Apply perspective transformation if needed
                        const displayRow = viewPerspective === 2 ? (boardRows - 1 - r) : r;
                        const displayCol = viewPerspective === 2 ? (boardCols - 1 - c) : c;

                        const x = displayCol * cellSize;
                        const y = displayRow * cellSize;
                        const color = getColor(value, minVal, maxVal);

                        return (
                            <rect
                                key={`${r}-${c}`}
                                x={x}
                                y={y}
                                width={cellSize}
                                height={cellSize}
                                fill={color}
                                className="heatmap-cell"
                            />
                        );
                    })
                ))}
            </svg>
        </div>
    );
};

export default HeatmapOverlay;
